import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { memo, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { ExpandMore } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import {
  ItalianAddressVM,
  ForeignAddressVM,
  PracticeDetailVM,
} from 'practice/view-models/practice-detail';
import FormItalian from './form-italian';
import FormForeign from './form-foreign';
import { PmiButton } from 'shell/components/shared/button';
import { Grid } from '@mui/material';
import ModalEditResidence from './edit-address';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useParams } from 'react-router-dom';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { AxiosPromise } from 'axios';
import { toast } from 'react-toastify';
import { EditResidenceVM } from 'practice/view-models/practice-edit-residence';

interface AccordionAddressProps {
  disabled?: boolean;
}

const AccordionAddressComponent = ({
  disabled = false,
}: AccordionAddressProps): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const formContext = useFormikContext<PracticeDetailVM>();
  const [modalEditResidence, setModalEditResidence] = useState<boolean>(false);

  const onChangeItalian = useEventCallback(
    (key: keyof ItalianAddressVM, value: ItalianAddressVM[keyof ItalianAddressVM]) => {
      formContext.setFieldValue('address', {
        ...(formContext.values.address || {}),
        italianAddress: {
          ...(formContext.values.address?.italianAddress || {}),
          [key]: value,
        },
      });
    }
  );

  const onChangeForeign = useEventCallback(
    (key: keyof ForeignAddressVM, value: ForeignAddressVM[keyof ForeignAddressVM]) => {
      formContext.setFieldValue('address', {
        ...(formContext.values.address || {}),
        foreignAddress: {
          ...(formContext.values.address?.foreignAddress || {}),
          [key]: value,
        },
      });
    }
  );

  const params = useParams();
  const {
    codiceEnte,
    idAnnoPratica,
    tipoRegistro,
    progressivoPratica,

    tipoPratica,
  } = params;

  const updateAddress = useHttpClient(
    `${__PRACTICE_API_URL__}/rettifica-pratiche/rettifica-indirizzo`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onConfirm = useEventCallback((values: EditResidenceVM) => {
    (
      updateAddress({
        data: {
          pratica: {
            key: {
              codiceEnte: codiceEnte,
              tipoRegistro: tipoRegistro,
              idAnnoPratica: Number(idAnnoPratica),
              progressivoPratica: Number(progressivoPratica),
            },
          },
          indirizzo:
            values?.italian && !values?.checkedResidence
              ? {
                  citta: values?.italian?.municipality?.description || undefined,
                  provincia: values?.italian?.municipality?.province || undefined,
                  cap: values?.italian?.cap || undefined,
                  nomeVia: values?.italian?.addressAuto?.description || undefined,
                  numeroCivico: values?.italian?.number || undefined,
                  esponente: values?.italian?.exponent || '',
                  sezione: values?.italian?.section || undefined,
                  flagIgnotaDimora: values?.italian?.isHomeless ? '1' : '0',
                  tipoOccupazione: values?.italian?.houseOccupationType || undefined,
                  flagAttualeResidenza: values?.flagAttualeResidenza ? '1' : '0',
                  sedeAgenziaEntrate: values?.italian?.officeContract || undefined,
                  dataRegistrazioneAgenziaEntrate:
                    values?.italian?.dateContract?.toFormat('yyyy-MM-dd') || undefined,
                  numeroRegistrazioneAgenziaEntrate: values?.italian?.numberContract || undefined,
                }
              : undefined,
          indirizzoEstero:
            values?.foreign && values?.checkedResidence
              ? {
                  statoEstero: values?.foreign?.foreignState?.province || undefined,
                  comune: values?.foreign?.municipality?.description || undefined,
                  codComune: values?.foreign?.municipality?.id
                    ? Number.parseInt(values?.foreign?.municipality?.id, 10)
                    : undefined,
                  contea: values?.foreign?.municipality?.province || undefined,
                  cap: values?.foreign?.cap || undefined,
                  indirizzo: values?.foreign?.address || undefined,
                  civico: values?.foreign?.civicNumber || undefined,
                  codiceConsolato: values?.foreign?.consulateCode?.id || undefined,
                  codiceTerritorio:
                    values?.foreign?.territory?.id && values?.foreign?.territory?.value
                      ? values?.foreign?.territory?.id
                      : undefined,
                }
              : undefined,
        },
      }) as AxiosPromise<void>
    ).then(() => {
      formContext.setFieldValue('address', {
        ...formContext?.values?.address,
        foreignAddress:
          values?.foreign && values?.checkedResidence
            ? {
                foreignState: values?.foreign?.foreignState?.value,
                foreignMunicipality: values?.foreign?.municipality?.description,
                foreignProvince: values?.foreign?.municipality?.province,
                cap: values?.foreign?.cap || undefined,
                toponymDenomination: values?.foreign?.address,
                civic: values?.foreign?.civicNumber,
                consulate: values?.foreign?.consulateCode?.value,
                territory: values?.foreign?.territory?.value,
                foreignStateId: values?.foreign?.foreignState?.id,
                foreignMunicipalityId: values?.foreign?.municipality?.id,
              }
            : undefined,
        italianAddress:
          values?.italian && !values?.checkedResidence
            ? {
                cap: values?.italian?.cap || '',
                civicNumber: values?.italian?.number?.toString() || '',
                exponent: values?.italian?.exponent || '',
                cat: values?.italian?.cat || '',
                isHomeless: values?.italian?.isHomeless || false,
                codVia: values?.italian?.address?.code?.toString() || '',
                codeStreet: values?.italian?.descrizioneSpecieToponimo || '',
                codTipoVia: values?.italian?.codTipoVia || '',
                date: values?.italian?.dateContract || null,
                houseOccupationType: values?.italian?.houseOccupationType || '',
                municipality: values?.italian?.municipality?.description || '',
                province: values?.italian?.municipality?.province || '',
                note: values?.italian?.note || '',
                section: values?.italian?.section || '',
                street: values?.italian?.addressAuto?.circulationArea?.descrizioneRidotta || '',
                number: values?.italian?.numberContract?.toString() || '',
                particle: values?.italian?.particle || '',
                sheet: values?.italian?.sheet || '',
                site: values?.italian?.officeContract || '',
                sub: values?.italian?.sub || '',
                withDatiCatastali: Boolean(
                  values?.italian?.section ||
                    values?.italian?.sheet ||
                    values?.italian?.particle ||
                    values?.italian?.sub ||
                    values?.italian?.cat
                ),
                withDatiAgenzia: Boolean(
                  values?.italian?.officeContract ||
                    values?.italian?.number ||
                    values?.italian?.dateContract
                ),
              }
            : undefined,
      });
      setModalEditResidence(false);
      toast.success('Operazione eseguita con successo');
    });
  });

  return (
    <>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="accordion-address-content"
          id="accordion-address-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Indirizzo</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {formContext.values.address?.isItalian ? (
            <FormItalian
              onChange={onChangeItalian}
              disabled={disabled}
              values={formContext.values.address?.italianAddress}
            />
          ) : (
            <FormForeign
              onChange={onChangeForeign}
              disabled={disabled}
              values={formContext.values.address?.foreignAddress}
              subscriptionAire={formContext.values.subscriptionAire}
            />
          )}
          {formContext?.values?.practice?.codState === '8' ||
          formContext?.values?.practice?.codState === '1' ? (
            <Grid container spacing={1} mt={2}>
              <Grid item xs={12} md={3}>
                <PmiButton
                  variant="outlined"
                  color="cdmBrandThird"
                  onClick={() => {
                    setModalEditResidence(true);
                  }}
                  disabled={
                    formContext.values.practice?.tipoCasoPratica === '2' ||
                    formContext.values.practice?.tipoCasoPratica === '1'
                  }
                  fullWidth
                >
                  MODIFICA
                </PmiButton>
              </Grid>
            </Grid>
          ) : undefined}
        </AccordionDetails>
      </Accordion>
      {modalEditResidence ? (
        <ModalEditResidence
          open={modalEditResidence}
          onClose={() => {
            setModalEditResidence(false);
          }}
          onConfirm={onConfirm}
          codeEnte={codiceEnte}
          typeRegister={tipoRegistro}
          intestatarioIsAnpr={formContext?.values?.accountholder?.isAire}
          yearId={idAnnoPratica}
          progressive={progressivoPratica}
          typePractice={tipoPratica}
          initialValues={{
            ...formContext?.values?.editAddress?.residence,
          }}
        />
      ) : undefined}
    </>
  );
};

AccordionAddressComponent.displayName = 'Practice Detail - Form - accordion address';

const AccordionAddress = memo(AccordionAddressComponent);

export default AccordionAddress;
